/**
 * Pure helpers for the order builder (Uusi tilaus): merging the three search sources
 * into vendor-grouped rows, turning a row into a cart line, grouping the cart per
 * vendor and validating it. No React, no axios: unit-testable in isolation.
 */

/** Same normalisation as maintenances.services.part_dedup.normalize_identifier_code. */
export const normalizeCode = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/** A code-shaped query: at least three characters and no whitespace (mirrors the backend). */
export const isCodeQuery = (query) => {
  const q = String(query || '').trim();
  return q.length >= 3 && !/\s/.test(q);
};

let keySeq = 0;
export const newLineKey = () => `l${Date.now().toString(36)}${(keySeq += 1).toString(36)}`;

export const draftStorageKey = (orgId) => `supplierOrderDraft:${orgId || 'none'}`;

export const SOURCE_RANK = { live: 0, exact: 1, alternate: 2, ean: 3, name: 4, register: 2 };

export const verificationKey = (integrationId, code) => `${integrationId}|${normalizeCode(code)}`;

const groupKeyForIntegration = (id) => `int-${id}`;

/** Resolved order integration from the builder context for an id. */
export const integrationById = (context, id) =>
  (context?.integrations || []).find((i) => i.id === Number(id)) || null;

const matchRank = (matchType) => {
  switch (matchType) {
    case 0:
      return SOURCE_RANK.exact;
    case 1:
      return SOURCE_RANK.alternate;
    case 2:
      return SOURCE_RANK.ean;
    default:
      return SOURCE_RANK.name;
  }
};

function partEan(part) {
  const ident = (part?.identifiers || []).find((i) => i.identifier_type === 'ean' && i.code);
  return ident ? ident.code : '';
}

function registerStock(part) {
  const info = part?.stock_info;
  if (!info || info.quantity_in_stock == null) return null;
  return { kind: 'register', quantity: parseFloat(info.quantity_in_stock) || 0, free: info.quantity_free != null ? parseFloat(info.quantity_free) : null };
}

/**
 * Merge register parts, catalogue rows and live hits into vendor-grouped rows.
 *
 * @param {object} args
 * @param {Array} args.register  /api/parts/ rows (with order_integration)
 * @param {Array} args.catalogue /api/catalogue-search/ rows (with order_integration, live_check_integration_id)
 * @param {object} args.live     {[integrationId]: {status, hits}}
 * @param {object} args.verifications {[verificationKey]: {status, hit}}
 * @param {object} args.context  builder context
 * @returns {Array<{key, integration, vendorName, orderable, rows: Array}>}
 */
export function buildResultGroups({ register = [], catalogue = [], live = {}, verifications = {}, context }) {
  const rowsByKey = new Map();

  const getRow = (groupKey, code, seed) => {
    const rowKey = `${groupKey}|${normalizeCode(code) || seed}`;
    let row = rowsByKey.get(rowKey);
    if (!row) {
      row = {
        rowKey,
        groupKey,
        integration: null,
        vendorId: null,
        vendorName: '',
        name: '',
        brand: '',
        code: code || '',
        ean: '',
        unit: '',
        purchasePrice: null,
        priceSource: null,
        listPrice: null,
        register: null,
        catalogue: null,
        live: null,
        liveCheckIntegrationId: null,
        stock: null,
        rank: SOURCE_RANK.name,
        matchedBy: 'name',
        oeNumber: '',
      };
      rowsByKey.set(rowKey, row);
    }
    return row;
  };

  // 1. Live hits (vendor API): the authoritative price and stock for API vendors.
  Object.entries(live).forEach(([integrationId, state]) => {
    const integration = integrationById(context, integrationId);
    (state?.hits || []).forEach((hit) => {
      const groupKey = groupKeyForIntegration(integrationId);
      const row = getRow(groupKey, hit.article_code, hit.article_code);
      row.integration = integration;
      row.vendorName = integration?.display_name || '';
      row.live = hit;
      row.code = row.code || hit.article_code;
      row.name = hit.name || row.name;
      row.brand = hit.brand || row.brand;
      if (hit.purchase_price != null) {
        row.purchasePrice = hit.purchase_price;
        row.priceSource = hit.price_source === 'catalogue' ? 'catalogue' : 'live';
      }
      row.stock = {
        kind: 'live',
        total: hit.total_stock,
        inStock: hit.in_stock,
        deliveryDays: hit.delivery_days,
        estimate: hit.availability_estimate || '',
      };
      row.rank = SOURCE_RANK.live;
      row.matchedBy = hit.matched_by || 'code';
      row.oeNumber = hit.oe_number || '';
    });
  });

  // 2. Catalogue rows (tuoteluettelot).
  catalogue.forEach((item) => {
    const orderIntegration = item.order_integration ? integrationById(context, item.order_integration.id) : null;
    const liveCheckId = item.live_check_integration_id;
    const liveCheck = liveCheckId ? integrationById(context, liveCheckId) : null;
    const integration = orderIntegration || liveCheck;
    const groupKey = integration ? groupKeyForIntegration(integration.id) : `vendor-${item.vendor_id}`;
    const row = getRow(groupKey, item.source_code, `c${item.item_id}`);
    row.integration = row.integration || integration;
    row.vendorId = item.vendor_id;
    row.vendorName = row.vendorName || item.vendor_name || integration?.display_name || '';
    row.catalogue = row.catalogue || item;
    row.code = row.code || item.source_code;
    row.name = row.name || item.name || '';
    row.brand = row.brand || item.brand || '';
    row.ean = row.ean || item.ean || '';
    row.unit = row.unit || item.unit || '';
    if (row.purchasePrice == null && item.purchase_price != null) {
      row.purchasePrice = item.purchase_price;
      row.priceSource = 'catalogue';
    }
    if (item.list_price != null) row.listPrice = item.list_price;
    if (!row.stock && item.total_stock != null) {
      row.stock = { kind: 'catalogue', total: parseFloat(item.total_stock), importedAt: item.last_imported_at };
    }
    if (!orderIntegration && liveCheck) row.liveCheckIntegrationId = liveCheck.id;
    const rank = matchRank(item.match_type);
    if (rank < row.rank) row.rank = rank;
  });

  // 3. Register parts (osarekisteri).
  register.forEach((part) => {
    const integration = part.order_integration ? integrationById(context, part.order_integration.id) : null;
    const groupKey = integration ? groupKeyForIntegration(integration.id) : `vendor-${part.vendor || 'none'}`;
    const code = part.primary_code || part.manufacturer_code || '';
    const row = getRow(groupKey, code, `p${part.id}`);
    row.integration = row.integration || integration;
    row.vendorId = row.vendorId || part.vendor || null;
    row.vendorName = row.vendorName || part.vendor_name || integration?.display_name || '';
    row.register = row.register || part;
    row.code = row.code || code;
    row.name = row.name || part.name || '';
    row.brand = row.brand || part.manufacturer_name || '';
    row.ean = row.ean || partEan(part);
    row.unit = row.unit || part.unit || '';
    if (row.purchasePrice == null && part.price != null) {
      row.purchasePrice = part.price;
      row.priceSource = 'register';
    }
    if (!row.stock) row.stock = registerStock(part);
    row.registerStock = registerStock(part);
    if (SOURCE_RANK.register < row.rank) row.rank = SOURCE_RANK.register;
  });

  // Orderability per row.
  rowsByKey.forEach((row) => {
    const integration = row.integration;
    row.orderable = false;
    row.reason = null;
    row.verifyOnAdd = false;
    if (!integration) {
      row.reason = 'no_channel';
      return;
    }
    if (row.live) {
      if (row.live.recognised) {
        row.orderable = true;
      } else {
        row.reason = 'unknown';
      }
      return;
    }
    const key = verificationKey(integration.id, row.code);
    const verification = verifications[key];
    if (verification?.status === 'ok') {
      if (!row.live && verification.hit) {
        row.live = verification.hit;
        if (verification.hit.purchase_price != null) {
          row.purchasePrice = verification.hit.purchase_price;
          row.priceSource = 'live';
        }
        row.stock = {
          kind: 'live',
          total: verification.hit.total_stock,
          inStock: verification.hit.in_stock,
          deliveryDays: verification.hit.delivery_days,
          estimate: verification.hit.availability_estimate || '',
        };
      }
      row.orderable = true;
      return;
    }
    if (row.liveCheckIntegrationId) {
      // A catalogue row of an API vendor: the vendor's API must recognise the code
      // before it is ordered. Verified when the row is added (one small call).
      if (verification?.status === 'checking') row.reason = 'checking';
      else if (verification?.status === 'unknown') row.reason = 'unknown';
      else if (verification?.status === 'failed') row.reason = 'failed';
      else {
        row.orderable = true;
        row.verifyOnAdd = true;
      }
      return;
    }
    row.orderable = true;
  });

  // Group by vendor, order rows within a group, groups by name.
  const groups = new Map();
  rowsByKey.forEach((row) => {
    let group = groups.get(row.groupKey);
    if (!group) {
      group = {
        key: row.groupKey,
        integration: row.integration,
        vendorId: row.vendorId || row.integration?.vendor_id || null,
        vendorName: row.vendorName || row.integration?.display_name || '',
        rows: [],
      };
      groups.set(row.groupKey, group);
    }
    if (!group.vendorName && row.vendorName) group.vendorName = row.vendorName;
    if (!group.vendorId && row.vendorId) group.vendorId = row.vendorId;
    group.rows.push(row);
  });
  const result = Array.from(groups.values());
  result.forEach((group) => {
    group.rows.sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name, 'fi'));
    group.orderable = !!group.integration;
  });
  result.sort((a, b) => {
    if (a.orderable !== b.orderable) return a.orderable ? -1 : 1;
    return a.vendorName.localeCompare(b.vendorName, 'fi');
  });
  return result;
}

/** Build a cart line from a result row. `verifiedHit` is the live hit a verify-on-add produced. */
export function lineFromRow(row, { quantity = '1', destination, verifiedHit = null }) {
  const integration = row.integration;
  const live = row.live || verifiedHit;
  let source;
  if (row.register) {
    source = { kind: 'part', part_id: row.register.id };
  } else if (row.catalogue) {
    source = {
      kind: 'catalogue_item',
      item_id: row.catalogue.item_id,
      live_integration_id: live && integration?.channel === 'api' ? integration.id : null,
    };
  } else {
    source = {
      kind: 'live',
      integration_id: integration.id,
      article_code: row.code,
      name: row.name,
      brand: row.brand,
      ean: row.ean,
    };
  }
  const purchasePrice = live && live.purchase_price != null ? live.purchase_price : row.purchasePrice;
  return {
    key: newLineKey(),
    source,
    integrationId: integration.id,
    name: row.name || row.code,
    code: row.code,
    brand: row.brand,
    unit: row.unit || '',
    quantity,
    purchasePrice: purchasePrice != null ? String(purchasePrice) : null,
    priceEditable: !(integration.channel === 'api' && live && live.purchase_price != null),
    priceSource: live && live.purchase_price != null ? (live.price_source === 'catalogue' ? 'catalogue' : 'live') : row.priceSource,
    liveConfirmed: !!live,
    destination: { ...destination },
  };
}

export function freeTextLine({ integration, code, name, brand, quantity, purchasePrice, hideFromRegister, destination }) {
  return {
    key: newLineKey(),
    source: {
      kind: 'free_text',
      integration_id: integration.id,
      code: code.trim(),
      name: name.trim(),
      brand: (brand || '').trim(),
      hide_from_register: !!hideFromRegister,
    },
    integrationId: integration.id,
    name: name.trim() || code.trim(),
    code: code.trim(),
    brand: (brand || '').trim(),
    unit: '',
    quantity,
    purchasePrice: purchasePrice ? String(purchasePrice) : null,
    priceEditable: true,
    priceSource: 'manual',
    liveConfirmed: false,
    destination: { ...destination },
    freeText: true,
  };
}

export const lineTotal = (line) => (parseFloat(line.purchasePrice) || 0) * (parseFloat(line.quantity) || 0);

export function cartTotals(lines) {
  const total = lines.reduce((sum, line) => sum + lineTotal(line), 0);
  const hasUnknownPrice = lines.some((line) => line.purchasePrice == null || line.purchasePrice === '');
  return { total, hasUnknownPrice, lineCount: lines.length };
}

/** Cart lines grouped per integration, in context order (one order each). */
export function cartGroups(lines, context) {
  const groups = new Map();
  lines.forEach((line) => {
    let group = groups.get(line.integrationId);
    if (!group) {
      group = { integration: integrationById(context, line.integrationId), lines: [] };
      groups.set(line.integrationId, group);
    }
    group.lines.push(line);
  });
  const list = Array.from(groups.values());
  list.forEach((g) => {
    g.totals = cartTotals(g.lines);
  });
  list.sort((a, b) => (a.integration?.display_name || '').localeCompare(b.integration?.display_name || '', 'fi'));
  return list;
}

/** Problem code for a line, or null when it can be sent. */
export function lineProblem(line, { newLahete }) {
  const qty = parseFloat(line.quantity);
  if (!(qty > 0)) return 'quantity';
  const dest = line.destination || {};
  if (dest.kind === 'maintenance' && !dest.maintenance_id) return 'destination';
  if (dest.kind === 'parts_sale' && !dest.parts_sale_id) return 'destination';
  if (dest.kind === 'new_parts_sale' && !newLahete?.customer?.id) return 'customer';
  return null;
}

/** The request body for POST /api/supplier-orders/create-from-cart/. */
export function buildCreatePayload({ lines, groups, newLahete, orderOptions }) {
  return {
    lines: lines.map((line) => ({
      key: line.key,
      source: line.source,
      quantity: String(line.quantity),
      purchase_price: line.purchasePrice != null && line.purchasePrice !== '' ? String(line.purchasePrice) : null,
      destination: {
        kind: line.destination.kind,
        maintenance_id: line.destination.maintenance_id || null,
        parts_sale_id: line.destination.parts_sale_id || null,
      },
    })),
    orders: groups.map((group) => {
      const options = orderOptions[group.integration.id] || {};
      const order = {
        integration_id: group.integration.id,
        line_keys: group.lines.map((line) => line.key),
        comment: options.comment || '',
      };
      if (group.integration.is_atoy) {
        order.tilaustyyppi = options.tilaustyyppi ?? 1;
        if (options.toimituskoodi) order.toimituskoodi = options.toimituskoodi;
      }
      return order;
    }),
    new_parts_sale: newLahete?.customer?.id ? { customer_id: newLahete.customer.id, notes: newLahete.notes || '' } : null,
  };
}

/** Destination summary counts for the cart footer chips. */
export function destinationSummary(lines) {
  const summary = new Map();
  lines.forEach((line) => {
    const dest = line.destination || {};
    let key;
    if (dest.kind === 'maintenance') key = `m-${dest.maintenance_id || 'none'}`;
    else if (dest.kind === 'parts_sale') key = `s-${dest.parts_sale_id || 'none'}`;
    else key = dest.kind || 'stock';
    const entry = summary.get(key) || { kind: dest.kind || 'stock', destination: dest, count: 0 };
    entry.count += 1;
    summary.set(key, entry);
  });
  return Array.from(summary.values());
}
